
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Code, Smartphone, Globe, BookOpen } from "lucide-react";
import VirtualAssistant from "./VirtualAssistant";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTranslatedText } from "@/hooks/useTranslate";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSiteSettings();

  // Usar el hook de traducción para todos los textos
  const homeText = useTranslatedText("Inicio");
  const servicesText = useTranslatedText("Servicios");
  const rentWebText = useTranslatedText("Renta Web");
  const projectsText = useTranslatedText("Proyectos");
  const blogText = useTranslatedText("Blog");
  const aboutText = useTranslatedText("Nosotros");
  const contactText = useTranslatedText("Contacto");
  const contactButtonText = useTranslatedText("Contactar");

  const isActive = (path: string) => location.pathname === path;

  const navigation = [
    { name: homeText, href: "/", icon: null },
    { name: servicesText, href: "/services", icon: Code },
    { name: rentWebText, href: "/renta-sitio-web", icon: Globe },
    { name: projectsText, href: "/projects", icon: Smartphone },
    { name: blogText, href: "/blog", icon: BookOpen },
    { name: aboutText, href: "/about", icon: Globe },
    { name: contactText, href: "/contact", icon: null },
  ];

  return (
    <>
      <header className="fixed top-0 w-full z-50 bg-techno-dark/90 backdrop-blur-md border-b border-purple-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              {settings.headerLogoUrl ? (
                <img 
                  src={settings.headerLogoUrl} 
                  alt={settings.siteName || "TECNOSYNC"} 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-10 h-10 bg-gradient-to-br from-techno-purple to-techno-accent rounded-lg flex items-center justify-center">
                    <Code className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xl font-bold text-white">{settings.siteName || "TECNOSYNC"}</span>
                </>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`transition-colors duration-200 ${
                    isActive(item.href)
                      ? "text-techno-purple font-medium"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-techno-purple to-techno-accent hover:from-techno-purple/90 hover:to-techno-accent/90 text-white font-medium px-6 py-2 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl">
                  {contactButtonText}
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white"
                aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-techno-dark/95 backdrop-blur-md border-t border-purple-500/20">
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center space-x-3 py-2 transition-colors duration-200 ${
                    isActive(item.href)
                      ? "text-techno-purple font-medium"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  {item.icon && <item.icon className="w-5 h-5" />}
                  <span>{item.name}</span>
                </Link>
              ))}
              <div className="pt-4 border-t border-purple-500/20">
                <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                  <Button className="bg-gradient-to-r from-techno-purple to-techno-accent hover:from-techno-purple/90 hover:to-techno-accent/90 text-white font-medium px-6 py-2 rounded-full w-full">
                    {contactButtonText}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
      
      <VirtualAssistant />
    </>
  );
};

export default Header;
